import { Moment } from 'moment';
import { QueryKey, queryOptions } from '@tanstack/react-query';
import { CalculationCompleted } from '../../effects/CalculationEffects';
import { EquipmentId } from '../../slices/equipments';
import { ParcelId } from '../../slices/parcels';
import { DeviceSessionId, EquipmentSessionId, ParcelSessionId } from '../../slices/sessions';
import { alerting, errorHasOccurred } from '../../slices/snacks';
import store from '../../store';
import { preNormalize } from '../../utils';
import { SessionTracks } from '../../utils/tracks';
import {
  getEquipmentSessionPoints,
  getEquipmentSessionWorkedArea,
  getEquipmentSessionsWorkedArea,
  getEquipmentsTracks,
  getForcedOrIgnoredParcelsOfDeviceSession,
  getParcelSessionWorkedArea,
  getParcelSessionsWorkedArea,
  getParcelsTracks,
  getSharedSession,
} from '../api';
import { queryClient } from '../queryClient';
import { transformEntityWithCamelCase } from '../schema';
import { sessionQueryKeys } from './keys/sessions';

type ApiError = { errors?: unknown[] };

type ForcedOrIgnoredParcel = {
  parcelId: ParcelId;
  ignore: boolean;
};

export function sharedSessionQueryOptions(id: string | null) {
  return queryOptions({
    queryKey: sessionQueryKeys.shared(id ?? ''),
    queryFn: async () => {
      try {
        return preNormalize(transformEntityWithCamelCase(await getSharedSession(id)));
      } catch (e) {
        if ((e as ApiError)?.errors) {
          throw e;
        }
        return errorHasOccurred(e, store.dispatch);
      }
    },
    enabled: !!id,
    // an unknown share id 404s, and retrying only delays the error page
    retry: false,
  });
}

export function forcedOrIgnoredParcelsQueryOptions(deviceSessionId: DeviceSessionId | undefined) {
  return queryOptions({
    queryKey: sessionQueryKeys.forcedOrIgnoredParcels(deviceSessionId ?? ''),
    queryFn: async () => {
      const response = await alerting(() => getForcedOrIgnoredParcelsOfDeviceSession(deviceSessionId), {
        dispatch: store.dispatch,
      });
      return Object.values(preNormalize(transformEntityWithCamelCase(response))) as ForcedOrIgnoredParcel[];
    },
    enabled: !!deviceSessionId,
  });
}

export function equipmentSessionPointsQueryOptions(sessionId?: EquipmentSessionId) {
  return queryOptions({
    queryKey: sessionQueryKeys.equipmentSessionPoints(sessionId),
    queryFn: async () => {
      const points = await alerting(() => getEquipmentSessionPoints(sessionId), { dispatch: store.dispatch });
      return points ?? null;
    },
    enabled: !!sessionId,
  });
}

const TRACK_STATUSES = ['computed', 'ended', 'active'];

function tracksParams(equipmentId: EquipmentId | undefined, start: string | undefined, end: string | undefined) {
  const params = new URLSearchParams();
  params.set('fromDate', start ?? '');
  params.set('toDate', end ?? '');
  params.set('equipmentID', String(equipmentId));
  TRACK_STATUSES.forEach((status) => params.append('status', status));
  return params.toString();
}

type TracksArgs = {
  equipmentId?: EquipmentId;
  startDate: Moment | null;
  endDate: Moment | null;
};

export function equipmentTracksQueryOptions({ equipmentId, startDate, endDate }: TracksArgs) {
  const start = startDate?.format();
  const end = endDate?.format();
  return queryOptions({
    queryKey: sessionQueryKeys.equipmentTracks({ equipmentId, start, end }),
    queryFn: async ({ signal }) => {
      const tracks = (await alerting(() => getEquipmentsTracks(tracksParams(equipmentId, start, end), signal), {
        dispatch: store.dispatch,
      })) as SessionTracks | undefined;
      return tracks ?? null;
    },
    enabled: !!equipmentId && !!start && !!end,
  });
}

export function parcelTracksQueryOptions({ equipmentId, startDate, endDate }: TracksArgs) {
  const start = startDate?.format();
  const end = endDate?.format();
  return queryOptions({
    queryKey: sessionQueryKeys.parcelTracks({ equipmentId, start, end }),
    queryFn: async ({ signal }) => {
      const tracks = (await alerting(() => getParcelsTracks(tracksParams(equipmentId, start, end), signal), {
        dispatch: store.dispatch,
      })) as SessionTracks | undefined;
      return tracks ?? null;
    },
    enabled: !!equipmentId && !!start && !!end,
  });
}

type WorkedAreaResponse = { worked_area?: string | null } | undefined;
type WorkedAreaPolygon = { geometry_area: string };

async function workedAreaPolygons(response: Promise<WorkedAreaResponse>): Promise<WorkedAreaPolygon[]> {
  const workedArea = (await response)?.worked_area;
  return workedArea ? [{ geometry_area: workedArea }] : [];
}

export function equipmentSessionWorkedAreaQueryOptions(id: EquipmentSessionId | undefined, enabled: boolean) {
  return queryOptions({
    queryKey: sessionQueryKeys.equipmentSessionWorkedArea(id),
    queryFn: ({ signal }) => workedAreaPolygons(getEquipmentSessionWorkedArea(id, signal)),
    enabled: enabled && !!id,
  });
}

export function equipmentSessionsWorkedAreaQueryOptions(ids: EquipmentSessionId[], enabled: boolean) {
  return queryOptions({
    queryKey: sessionQueryKeys.equipmentSessionsWorkedArea(ids),
    queryFn: ({ signal }) => workedAreaPolygons(getEquipmentSessionsWorkedArea(ids, signal)),
    enabled: enabled && ids.length > 0,
  });
}

export function parcelSessionWorkedAreaQueryOptions(id: ParcelSessionId | undefined, enabled: boolean) {
  return queryOptions({
    queryKey: sessionQueryKeys.parcelSessionWorkedArea(id),
    queryFn: ({ signal }) => workedAreaPolygons(getParcelSessionWorkedArea(id, signal)),
    enabled: enabled && !!id,
  });
}

export function parcelSessionsWorkedAreaQueryOptions(ids: ParcelSessionId[], enabled: boolean) {
  return queryOptions({
    queryKey: sessionQueryKeys.parcelSessionsWorkedArea(ids),
    queryFn: ({ signal }) => workedAreaPolygons(getParcelSessionsWorkedArea(ids, signal)),
    enabled: enabled && ids.length > 0,
  });
}

/**
 * Without an event (the socket catching up after a disconnection) every equipment worked area is invalidated. Events
 * only name equipment sessions, so parcel worked areas are always invalidated wholesale.
 */
export function invalidateWorkedAreasAfterCalculation(event?: CalculationCompleted) {
  const calculatedIds = event && (event.equipment_session_ids || []);
  const predicate = ({ queryKey }: { queryKey: QueryKey }) => {
    if (!calculatedIds) return true;
    const { id, ids = [id] } = queryKey[2] as { id?: EquipmentSessionId; ids?: EquipmentSessionId[] };
    return ids.some((sessionId) => sessionId !== undefined && calculatedIds.includes(sessionId));
  };
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: sessionQueryKeys.equipmentSessionWorkedArea._def, predicate }),
    queryClient.invalidateQueries({ queryKey: sessionQueryKeys.equipmentSessionsWorkedArea._def, predicate }),
    queryClient.invalidateQueries({ queryKey: sessionQueryKeys.parcelSessionWorkedArea._def }),
    queryClient.invalidateQueries({ queryKey: sessionQueryKeys.parcelSessionsWorkedArea._def }),
  ]);
}
